/**
 * Copyright 2025 Design Barn Inc.
 */

import { IconWarningOutline } from '@lottiefiles/icons';
import { lottiefilesLightToken } from '@lottiefiles/tokens';
import cn from 'classnames';
import type { ReactNode } from 'react';
import React from 'react';

export interface InputProps {
  errorText?: string;
  helperText?: string;
  label?: string;
  placeholder?: string;
  rightElement?: ReactNode;
  type: 'text' | 'password' | 'email';
}

export default function InputComponent({
  errorText,
  helperText,
  label,
  placeholder,
  rightElement,
  type,
  ...props
}: InputProps): React.ReactElement {
  const borderColor = cn('border-default ', {
    'border-action-critical': errorText,
    'border-subtle': !errorText,
  });

  let messageComponent;

  if (errorText) {
    messageComponent = (
      <div className="mt-[12px] flex items-center space-x-2">
        <IconWarningOutline color={lottiefilesLightToken.color.actionCritical} />
        <p className="text-xs text-red-500">{errorText}</p>
      </div>
    );
  } else if (helperText) {
    messageComponent = (
      <div className="mt-[12px] flex items-center space-x-2">
        <p className="text-xs text-gray-400">{helperText}</p>
      </div>
    );
  }

  return (
    <div>
      <div
        className={cn(
          `has-[input:focus]:border-action-focus flex h-11 flex-1 items-center rounded-sm border-solid bg-gray-50 p-[12px] text-base ${borderColor}`,
        )}
      >
        <input
          type={type}
          placeholder={placeholder}
          aria-label={label}
          className="text-primary placeholder:text-placeholder h-full flex-1 border-none bg-transparent outline-none"
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
        {rightElement}
      </div>

      {messageComponent}
    </div>
  );
}
